import { Component, OnInit, OnDestroy, ViewChild, ViewContainerRef } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { AlarmService } from "../services/alarm.service";
import { Utility } from "../utils/app-utils";
import { LocationStrategy } from "@angular/common";
import { Alarm } from "src/models/alarm.model";
import { GridStack, GridStackElement, GridStackNode, GridStackOptions } from "gridstack";
import "gridstack/dist/gridstack-all";
import { WidgetService } from "../services/widget.service";
import { AppSessionStorage } from "../utils/app-session-storage-utils";
import { AppConfigService } from "../app-config.service";
import { FeedsComponent } from "../flip-cards/feeds/feeds.component";
import { InsightsComponent } from "../flip-cards/insights/insights.component";
import { IncidentsComponent } from "../flip-cards/incidents/incidents.component";
import { SendAlarmComponent } from "../send-alarm/send-alarm.component";
import { MapComponent } from "../asset-viewer/map/map.component";
import { AskRamsysComponent } from "../ask-ramsys/ask-ramsys.component";
import { AlarmProgressComponent } from "../alarm-progress/alarm-progress.component";
import { WorkflowComponent } from "../workflow/workflow.component";
import { AssetViewerComponent } from "../asset-viewer/asset-viewer.component";
import { ActionHistoryComponent } from "../action-history/action-history.component";
import { AlarmsComponent } from "../alarms/alarms.component";
import { ChatBarComponent } from "../chat-bar/chat-bar.component";
import { BranchControlsComponent } from "../branch-controls/branch-controls.component";
import { CreateTaskComponent } from "../create-task/create-task.component";
import { FullscreenVideoComponent } from "../fullscreen-video/fullscreen-video.component";
import { VideoStreamService } from "../services/video-stream.service";
import { SingleVideoFeedComponent } from "../single-video-feed/single-video-feed.component";
import { VncDialogComponent } from "../vnc-dialog/vnc-dialog.component";
import { GridItem, arrangeStandardLayout } from "./layout.util";
import { ZoneComponent } from "../asset-viewer/zone/zone.component";
import { ConfidenceMonitoringService } from "../services/confidence-monitoring.service";
import { ControllersComponent } from "../controllers/controllers.component";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, OnDestroy {
  public assetDoc: Subject<any> = new Subject();
  selectedAlarmSubscriber: Subscription;
  selectedAlarm: Alarm;

  @ViewChild("gridContainer", { read: ViewContainerRef })
  gridContainer: ViewContainerRef;

  // Store component references for dynamic widgets
  private widgetComponents: Map<string, any> = new Map();

  // Overlay widget properties
  public overlayWidgetVisible: boolean = false;
  public overlayWidgetData: any = null;
  private activeOverlayWidgetId: string | null = null;

  // Grid/Widgets Settings
  private grid: GridStack;
  private LAYOUT_STORAGE_KEY = "gridLayout";
  gridReady: boolean = false;

  private readonly WIDGET_COMPONENT_SELECTORS =
    "app-feeds, app-insights, app-incidents, app-send-alarm, app-map, app-ask-ramsys, app-asset-viewer, app-alarm-progress, app-workflow, app-action-history, app-alarms, app-chat-bar, app-branch-controls, app-create-task, app-single-video-feed, app-vnc-dialog, app-zone, app-controllers";

  private readonly SYSTEM_LAYOUT: GridItem[] = [
    { id: 'alerts', x: 0, y: 0, w: 2, h: 12, minW: 2, minH: 4, maxH: 12, removable: true },
    { id: 'video-feeds', x: 2, y: 0, w: 5, h: 5, minW: 3, minH: 4, maxH: 12, removable: true },
    { id: 'alarm-tabs', x: 2, y: 5, w: 5, h: 7, minW: 4, minH: 5, maxH: 12, removable: true },
    { id: 'insights', x: 7, y: 0, w: 5, h: 6, minW: 4, minH: 6, maxH: 7, removable: true },
    { id: 'chat', x: 7, y: 6, w: 5, h: 1, minW: 2, minH: 1, maxH: 1, removable: true },
    { id: 'live-incidents', x: 7, y: 7, w: 5, h: 5, minW: 4, minH: 5, maxH: 6, removable: true }
  ];

  layoutChangedSubscription: Subscription;
  widgetAddedSubscription: Subscription;
  gridLockedSubscription: Subscription;
  private isGridLocked: boolean = false;
  public isPrivacyMode: boolean = false;
  private privacyModeSubscription: Subscription;

  constructor(
    private alarmService: AlarmService,
    private locationStrategy: LocationStrategy,
    public widgetService: WidgetService,
    public appConfigService: AppConfigService,
    public videoStreamService: VideoStreamService,
    public confidenceMonitoringService: ConfidenceMonitoringService,
  ) { }

  async ngOnInit() {
    this.isUserLayoutRestricted();
    this.initializeGrid();
    this.selectedAlarmListener();
    this.preventBackButton();

    this.layoutChangedSubscription = this.widgetService.layoutChangedListener().subscribe((changed: boolean) => {
      if (changed) {
        this.fetchLayout();
      }
    });

    this.widgetAddedSubscription = this.widgetService.widgetAddedListener().subscribe((widget: any) => {
      if (widget) {
        let newWidget = {
          id: widget,
          w: 5,
          h: 5,
          autoPosition: true,
          removable: !this.isGridLocked,
          noResize: false,
          noMove: false,
          locked: false,
          minW: 3,
          minH: 4,
          maxH: 12,
        };

        // Set specific dimensions for certain widgets
        if (widget === "alerts") {
          newWidget.w = 2;
          newWidget.h = 4;
          newWidget.minW = 2;
          newWidget.minH = 4;
          newWidget.maxH = 12;
        } else if (widget === "chat") {
          newWidget.w = 2;
          newWidget.h = 1;
          newWidget.minW = 2;
          newWidget.minH = 1;
          newWidget.maxH = 1;
        }

        this.grid.addWidget(newWidget);
      }
    });

    this.gridLockedSubscription = this.widgetService.gridLockedListener().subscribe((locked: boolean) => {
      this.isGridLocked = locked;
      locked ? this.grid.disable() : this.grid.enable();

      setTimeout(() => {
        this.processAllWidgets();
        this.applyRemovableStateToAllItems();
      }, 50);
    });

    this.privacyModeSubscription = this.widgetService.privacyModeListener().subscribe((privacyMode: boolean) => {
      this.isPrivacyMode = privacyMode;
    });

    this.isGridLocked = localStorage.getItem("gridLocked") === "true";
    this.isGridLocked ? this.grid.disable() : this.grid.enable();
    this.isPrivacyMode = localStorage.getItem("privacyMode") === "true";
  }

  async initializeGrid() {
    const options: GridStackOptions = {
      float: true,
      animate: true,
      minRow: 1,
      row: 12,
      maxRow: 12,
      column: 12,
      margin: 5,
      cellHeight: (window.innerHeight * 0.93) / 12,
      resizable: {
        handles: "n, e, s, w, ne, se, sw, nw",
        autoHide: true,
      },
      draggable: {
        handle: ".grid-stack-item-content",
        scroll: false,
      },
      staticGrid: this.isUserLayoutRestricted(),
      removable: false,
      disableDrag: false,
      disableResize: false,
      rtl: false,
      styleInHead: true,
    };

    this.grid = GridStack.init(options);
    await this.fetchLayout();
    this.setupGridEvents();
  }

  async fetchLayout() {
    // Clean up any existing components before loading new layout
    this.cleanupAllWidgetComponents();

    const userLayout = await this.widgetService.getActiveLayout();

    if (userLayout && userLayout?.layout?.length > 0) {
      // If category is 'standard', arrange widgets intelligently
      // if (userLayout.category === 'standard') {
      // 	const arrangedLayout = arrangeStandardLayout(
      // 		userLayout?.layout,
      // 		this.widgetService.availableWidgets,
      // 		this.SYSTEM_LAYOUT
      // 	);
      // 	this.grid.load(arrangedLayout);
      // } else {
      this.grid.load(userLayout.layout);
      // }
    } else {
      this.grid.load(this.SYSTEM_LAYOUT);
    }
    this.gridReady = true;

    // Process all existing widgets to ensure they have content
    setTimeout(() => {
      this.processAllWidgets();
    }, 100);
  }

  private cleanupAllWidgetComponents(): void {
    // Destroy all tracked widget components
    this.widgetComponents.forEach((componentRef) => componentRef?.destroy());
    this.widgetComponents.clear();
  }

  private processAllWidgets(): void {
    const allItems = this.grid.engine.nodes;
    allItems.forEach((item) => {
      this.processWidget(item);
    });
  }

  private processWidget(item: any): void {
    const widgetElement = document.querySelector(`[gs-id="${item.id}"]`,) as HTMLElement;
    if (widgetElement) {
      const contentDiv = widgetElement.querySelector(".grid-stack-item-content") as HTMLElement;
      // Inject widget content only if it doesn't already exist
      if (
        contentDiv && !contentDiv.querySelector(this.WIDGET_COMPONENT_SELECTORS)
      ) {
        this.injectWidgetContent(item, contentDiv);
      }

      // Update delete icon visibility based on current removable state
      this.updateDeleteIconVisibility(item.id, widgetElement);
    }
  }

  private updateDeleteIconVisibility(widgetId: string, widgetElement: HTMLElement): void {
    const canRemove = this.canRemoveWidget(widgetId);
    let icon = widgetElement.querySelector(`i[data-widget-id="${widgetId}"]`) as HTMLElement;

    if (canRemove) {
      if (!icon) {
        this.createDeleteIconForWidget(widgetId, widgetElement);
      } else {
        icon.style.display = "block";
      }
    } else {
      if (icon) {
        icon.style.display = "none";
      }
    }
  }

  private injectWidgetContent(item: any, contentDiv: HTMLElement): void {
    contentDiv.style.borderRadius = "10px";

    // Clean up any existing component for this widget to prevent duplicates
    if (this.widgetComponents.has(item.id)) {
      const existingComponentRef = this.widgetComponents.get(item.id);
      if (existingComponentRef) {
        existingComponentRef.destroy();
      }
      this.widgetComponents.delete(item.id);
    }

    // Create Angular component using ViewContainerRef
    let componentRef: any = null;
    switch (item.id) {
      case "alerts":
        componentRef = this.gridContainer.createComponent(AlarmsComponent);
        break;
      case "video-feeds":
        componentRef = this.gridContainer.createComponent(FeedsComponent);
        break;
      case "insights":
        componentRef = this.gridContainer.createComponent(InsightsComponent);
        // Get current alarm from service if component's selectedAlarm is not set
        const currentAlarm =
          this.selectedAlarm || this.alarmService?.selectedAlarm;
        if (currentAlarm) {
          componentRef.instance.selectedAlarm = currentAlarm;
        }
        // Store reference to update later when alarm is selected
        this.widgetComponents.set("insights", componentRef);
        componentRef.changeDetectorRef.detectChanges();
        break;
      case "live-incidents":
        componentRef = this.gridContainer.createComponent(IncidentsComponent);
        componentRef.instance.alarm = this.selectedAlarm;
        break;
      case "send-alarm":
        componentRef = this.gridContainer.createComponent(SendAlarmComponent);
        break;
      case "map":
        componentRef = this.gridContainer.createComponent(MapComponent);
        componentRef.instance.shouldShowSearchInput = true;
        break;
      case "ask-ramsys":
        componentRef = this.gridContainer.createComponent(AskRamsysComponent);
        this.alarmService.isAskRamsysActive = true;
        break;
      case "alarm-progress":
        componentRef = this.gridContainer.createComponent(
          AlarmProgressComponent,
        );
        componentRef.instance.alarm = this.selectedAlarm;
        break;
      case "workflow":
        componentRef = this.gridContainer.createComponent(WorkflowComponent);
        this.confidenceMonitoringService.isStandaloneWorkflow = true;
        componentRef.instance.alarm = this.selectedAlarm;
        break;
      case "alarm-tabs":
        componentRef = this.gridContainer.createComponent(AssetViewerComponent);
        componentRef.instance.assetDoc = this.assetDoc;
        setTimeout(() => {
          this.alarmService.selectAlarm(this.selectedAlarm);
        }, 100);
        this.alarmService.activeTabIndex.next(0);
        break;
      case "action-history":
        componentRef = this.gridContainer.createComponent(ActionHistoryComponent);
        if (this.selectedAlarm) {
          componentRef.instance.alarm = this.selectedAlarm;
          setTimeout(() => {
            this.alarmService.selectAlarm(this.selectedAlarm);
          }, 100);
        }
        break;
      case "chat":
        componentRef = this.gridContainer.createComponent(ChatBarComponent);
        break;
      case "branch-controls":
        componentRef = this.gridContainer.createComponent(BranchControlsComponent);
        break;
      case "tasks":
        componentRef = this.gridContainer.createComponent(CreateTaskComponent);
        break;
      case "single-video-feed":
        componentRef = this.gridContainer.createComponent(SingleVideoFeedComponent);
        if (this.selectedAlarm) {
          setTimeout(() => {
            this.alarmService.selectAlarm(this.selectedAlarm);
          }, 100);
        }
        break;
      case "vnc":
        componentRef = this.gridContainer.createComponent(VncDialogComponent);
        break;
      case "zone":
        componentRef = this.gridContainer.createComponent(ZoneComponent);
        componentRef.instance.alarm = this.selectedAlarm;
        break;
      case "controllers":
        componentRef = this.gridContainer.createComponent(ControllersComponent);
        setTimeout(() => {
          this.alarmService.selectAlarm(this.selectedAlarm);
        }, 100);
        break;
    }

    if (componentRef) {
      // Store ALL component references for proper cleanup on removal
      this.widgetComponents.set(item.id, componentRef);
      contentDiv.appendChild(componentRef.location.nativeElement);
    }
  }

  private setupGridEvents(): void {
    this.grid.on("change", (_event: Event, _items: GridStackNode[]) => {
      this.saveLayout();
    });

    this.grid.on("removed", (_event: Event, _items: GridStackNode[]) => {
      // Clean up component references for removed widgets
      _items.forEach((item) => {
        const componentRef = this.widgetComponents.get(item.id);
        if (componentRef) {
          componentRef.destroy();
          this.widgetComponents.delete(item.id);
        }
      });

      // Set selectedAlarm to null if 'alerts' widget is removed
      if (_items.some((item) => item.id === "alerts")) {
        this.selectedAlarm = null;
        this.alarmService.selectAlarm(null);
        this.videoStreamService.setEmptyVideoSources();

        // Reset alarm-dependent widgets to clear old alarm state
        const widgetsToReset = [
          "alarm-progress",
          "workflow",
          "action-history",
          "insights",
          "live-incidents",
          "zone",
          "controllers",
        ];
        widgetsToReset.forEach((id) => {
          const compRef = this.widgetComponents.get(id);
          if (compRef) {
            if (compRef.instance.updateAlarm) {
              compRef.instance.updateAlarm(null);
            }
            if ("selectedAlarm" in compRef.instance) {
              compRef.instance.selectedAlarm = null;
              compRef.changeDetectorRef.detectChanges();
            }
          }
        });
      }

      if (_items.some((item) => item.id === "ask-ramsys")) {
        this.alarmService.isAskRamsysActive = false;
      }

      if (_items.some((item) => item.id === "alarm-tabs")) {
        this.alarmService.activeAlarmsTab = "progress";
      }

      this.saveLayout();
    });

    this.grid.on("added", (_event: Event, _items: GridStackNode[]) => {
      // Inject content for newly added widgets
      _items.forEach((item) => {
        this.processWidget(item);
      });

      this.saveLayout();
    });

    // Resize event handlers to handle video-feeds iframe resizing
    this.grid.on("resizestart", (_event: Event, _items: GridStackNode[]) => {
      const iframes = document.querySelectorAll(".iframe_container iframe");
      iframes.forEach((iframe: HTMLElement) => {
        iframe.style.pointerEvents = "none";
      });

      // Close all map marker popups
      const popups = document.querySelectorAll(".leaflet-popup");
      if (popups.length > 0) {
        const mapContainer = document.querySelector(".leaflet-container");
        if (mapContainer) {
          const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          mapContainer.dispatchEvent(clickEvent);
        }

        setTimeout(() => {
          document.querySelectorAll(".leaflet-popup").forEach((popup) => {
            popup.parentNode?.removeChild(popup);
          });
        }, 10);
      }
    });

    this.grid.on("resizestop", (_event: Event, _items: GridStackNode[]) => {
      const iframes = document.querySelectorAll(".iframe_container iframe");
      iframes.forEach((iframe: HTMLElement) => {
        iframe.style.pointerEvents = "auto";
      });
    });
  }

  private saveLayout(): void {
    // Get the current grid items
    const currentItems = this.grid.engine.nodes;
    // Create a map of current items by id for quick lookup
    const currentItemsMap = new Map(
      currentItems.map((item) => [item.id, item]),
    );
    // Get the original layout items
    const originalLayout = this.grid.save() as GridItem[];

    // Update the layout with current positions while preserving all original properties
    const updatedLayout = originalLayout.map((originalItem) => {
      const currentItem = currentItemsMap.get(originalItem.id);
      if (currentItem) {
        // Only include the properties we want to save
        return {
          id: originalItem.id,
          x: currentItem.x,
          y: currentItem.y,
          w: currentItem.w,
          h: currentItem.h,
          minW: originalItem.minW,
          minH: originalItem.minH,
          maxH: originalItem.maxH,
          removable: originalItem.removable || true,
          noMove: originalItem.noMove || false,
          noResize: originalItem.noResize || false,
          locked: originalItem.locked || false,
        };
      }
      return originalItem;
    });

    localStorage.setItem(
      this.LAYOUT_STORAGE_KEY,
      JSON.stringify(updatedLayout),
    );
  }

  isUserLayoutRestricted() {
    if (!this.appConfigService.appConfig.WIDGETS) {
      return true;
    }
    const user = AppSessionStorage.getUser();
    const layoutSettings = user?.layoutSettings;
    if (
      layoutSettings &&
      !layoutSettings.allowCustomizeLayout &&
      !layoutSettings.allowModifyDefaultLayout
    ) {
      return true;
    }
    return false;
  }

  selectedAlarmListener() {
    this.selectedAlarmSubscriber = this.alarmService
      .getOnAlarmSelectedListener()
      .subscribe((alarm: Alarm) => {
        this.selectedAlarm = alarm;
        // Update alarm-progress widget if it exists
        const alarmProgressComponent =
          this.widgetComponents.get("alarm-progress");
        if (
          alarmProgressComponent &&
          alarmProgressComponent.instance.updateAlarm
        ) {
          alarmProgressComponent.instance.updateAlarm(alarm);
        }

        // Update workflow widget if it exists
        const workflowComponent = this.widgetComponents.get("workflow");
        if (workflowComponent && workflowComponent.instance.updateAlarm) {
          workflowComponent.instance.updateAlarm(alarm);
        }

        // Update action-history widget if it exists
        const actionHistoryComponent =
          this.widgetComponents.get("action-history");
        if (
          actionHistoryComponent &&
          actionHistoryComponent.instance.updateAlarm
        ) {
          actionHistoryComponent.instance.updateAlarm(alarm);
        }

        // Update insights widget if it exists
        const insightsComponent = this.widgetComponents.get("insights");
        if (insightsComponent) {
          insightsComponent.instance.selectedAlarm = alarm;
          insightsComponent.changeDetectorRef.detectChanges();
        }

        // Update live-incidents widget if it exists
        const liveIncidentsComponent =
          this.widgetComponents.get("live-incidents");
        if (liveIncidentsComponent) {
          liveIncidentsComponent.instance.selectedAlarm = alarm;
          liveIncidentsComponent.changeDetectorRef.detectChanges();
        }

        // Update zone widget if it exists
        const zoneComponent = this.widgetComponents.get("zone");
        if (zoneComponent && zoneComponent.instance.updateAlarm) {
          zoneComponent.instance.updateAlarm(alarm);
        }
      });
  }

  ngOnDestroy() {
    this.selectedAlarmSubscriber = Utility.Unsubscribe(
      this.selectedAlarmSubscriber,
    );
    if (this.grid) {
      this.grid.destroy();
    }
    Utility.Unsubscribe(this.layoutChangedSubscription);
    Utility.Unsubscribe(this.widgetAddedSubscription);
    Utility.Unsubscribe(this.gridLockedSubscription);
    Utility.Unsubscribe(this.privacyModeSubscription);

    // Clean up all widget component references
    this.cleanupAllWidgetComponents();
    this.closeOverlayWidget();
    this.alarmService.selectAlarm(null);
  }

  // Disable back button
  preventBackButton() {
    history.pushState(null, null, location.href);
    this.locationStrategy.onPopState(() => {
      history.pushState(null, null, location.href);
    });
  }

  removeWidget(event: Event, widgetId: string): void {
    event.preventDefault();
    event.stopPropagation();

    // Find the grid item by widget ID and remove it
    // Cleanup will be handled by the 'removed' event handler
    const element = document.querySelector(`[gs-id="${widgetId}"]`);
    if (element) {
      this.grid.removeWidget(element as GridStackElement);
    }
  }

  canRemoveWidget(widgetId: string): boolean {
    // If grid is locked, no widgets can be removed
    if (this.isGridLocked) {
      return false;
    }

    if (this.isUserLayoutRestricted()) {
      return false;
    }

    const widget = this.SYSTEM_LAYOUT.find((item) => item.id === widgetId);
    // If widget is in SYSTEM_LAYOUT Restricted, use its removable property
    if (widget) {
      return widget.removable ?? false;
    }
    // If widget is not in SYSTEM_LAYOUT (dynamically added), it should be removable by default
    return true;
  }

  private applyRemovableStateToAllItems(): void {
    const items = this.grid.engine.nodes;
    items.forEach((item) => {
      // Determine if this widget should be removable based on grid lock and user restrictions
      const shouldBeRemovable =
        !this.isGridLocked && !this.isUserLayoutRestricted();
      // Update the removable property on the grid item
      (item as any).removable = shouldBeRemovable;
      // Update the DOM element to reflect the change
      const element = document.querySelector(`[gs-id="${item.id}"]`);
      if (element) {
        const deleteIcon = element.querySelector(
          `i[data-widget-id="${item.id}"]`,
        ) as HTMLElement;
        if (deleteIcon) {
          deleteIcon.style.display = shouldBeRemovable ? "block" : "none";
        } else {
          // For widgets without delete icons, create them if they should be removable
          if (shouldBeRemovable) {
            this.createDeleteIconForWidget(item.id, element as HTMLElement);
          }
        }
      }
    });
  }

  private createDeleteIcon(widgetId: string): HTMLElement {
    // Create simple minus circle icon
    const icon = document.createElement("i");
    icon.className = "fa fa-minus-circle";
    icon.style.color = "var(--text-color)";
    icon.style.fontSize = "16px";
    icon.style.pointerEvents = "auto";

    // Force positioning with inline styles
    icon.style.position = "absolute";
    icon.style.top = "-3px";
    icon.style.right = "20px";
    icon.style.zIndex = "99999";
    icon.style.cursor = "pointer";

    // Add click event listener
    icon.addEventListener("click", (event) => {
      this.removeWidget(event, widgetId);
    });

    return icon;
  }

  private createDeleteIconForWidget(
    widgetId: string,
    element: HTMLElement,
  ): void {
    let icon = element.querySelector(
      `i[data-widget-id="${widgetId}"]`,
    ) as HTMLElement;
    if (!icon) {
      icon = this.createDeleteIcon(widgetId);
      icon.setAttribute("data-widget-id", widgetId);

      // Append icon to the widget container
      element.appendChild(icon);
    }

    // Update visibility based on current lock state
    const shouldShow = this.canRemoveWidget(widgetId);
    icon.style.display = shouldShow ? "block" : "none";
  }

  // Overlay widget methods
  public showOverlayWidget(widgetId: string, data: any): void {
    // Close any existing overlay widget first
    this.closeOverlayWidget();

    this.overlayWidgetData = data;
    this.overlayWidgetVisible = true;
    this.createOverlayWidget(widgetId, data);
  }

  public closeOverlayWidget(): void {
    this.overlayWidgetVisible = false;

    if (!this.activeOverlayWidgetId) {
      return;
    }

    const componentRef = this.widgetComponents.get(this.activeOverlayWidgetId);
    if (componentRef) {
      componentRef.destroy();
      this.widgetComponents.delete(this.activeOverlayWidgetId);
    }

    this.activeOverlayWidgetId = null;
  }

  private createOverlayWidget(widgetId: string, data: any): void {
    const componentRef = this.gridContainer.createComponent(
      FullscreenVideoComponent,
    );
    componentRef.instance.videoFeed = data;
    componentRef.instance.isVisible = true;
    componentRef.instance.closeFullscreen.subscribe(() => {
      this.closeOverlayWidget();
    });

    // Append directly to body without overlay container to avoid blocking
    document.body.appendChild(componentRef.location.nativeElement);

    // Store reference for cleanup
    this.widgetComponents.set(widgetId, componentRef);
    this.activeOverlayWidgetId = widgetId;
  }
}
